import { Component, OnInit, ViewChild } from '@angular/core';
import { FormComponent } from '@ov-suite/ui';
import { CompiledFieldData, HasId } from '@ov-suite/ov-metadata';
import { hasFeaturePermission, PermissionAction } from '@ov-suite/authguard-angular';
import { Profile, UserModel } from '@ov-suite/models-idm';
import { OvAutoService } from '@ov-suite/services';
import { DomainModel } from '@ov-suite/models-admin';
import { DomainService } from '@ov-suite/helpers-angular';
import { FeatureKey } from '@ov-suite/helpers-shared/lib/feature.enum';
import { AuthenticationService } from '@ov-suite/authguard-angular/lib/authentication/authentication.service';
import { BaseUserToken } from '@ov-suite/helpers-shared/lib/user.interface';
import swal from 'sweetalert2';
import { gql } from '@apollo/client';
import { getUrl } from '@ov-suite/helpers-angular/lib/get-url.helper';
import { AWSStorageService } from '../../services/aws-storage.service';

export class DetectChanges {
  newChanges: CompiledFieldData[] = [];

  diff(formData: unknown, field: CompiledFieldData): void {
    if (formData && field) {
      if (formData[field.propertyKey] !== field.value) {
        this.newChanges.push(field);
      } else {
        this.removeField(field);
      }
    }
  }

  removeField(field: CompiledFieldData): void {
    this.newChanges = this.newChanges.filter(data => data.propertyKey !== field.propertyKey);
  }
}

interface FileUpload {
  target: {
    files: {
      item: (number: number) => File;
      length: number;
    };
  };
}

@Component({
  selector: 'ov-suite-user-cmp',
  templateUrl: 'user.component.html',
})
export class UserComponent implements OnInit {
  loading: boolean;

  profileForm = Profile;

  userData: Profile;

  domainList: DomainModel[] = [];

  currentDomainId: number;

  @ViewChild('form') form: FormComponent;

  detect = new DetectChanges();

  interval = null;

  canUpdateProfile = false;

  user: BaseUserToken;

  avatarUrl: string;

  constructor(
    private readonly storageService: AWSStorageService,
    private readonly ovAutoService: OvAutoService,
    public readonly domainService: DomainService,
    private readonly authenticationService: AuthenticationService,
  ) {}

  ngOnInit(): void {
    this.domainList = this.domainService.allDomains.value;
    this.user = this.authenticationService.user.value;
    this.userData = Profile.fromUser(this.user);
    this.checkPermissions();
    this.avatarUrl = `${getUrl('api')}/api/authentication/image/${this.user.id}`;
  }

  checkPermissions() {
    hasFeaturePermission(this.user, FeatureKey.VIEW_PROFILE, PermissionAction.update).then(res => {
      this.canUpdateProfile = res;
    });
  }

  async onSave() {
    await this.form.submit(true);
    if (!(await this.updateUser())) {
      swal.fire({
        title: 'Error!',
        text: 'Something went Wrong',
        type: 'error',
        timer: 2000,
      });
      return;
    }

    if (!(await this.handlePasswords())) {
      swal.fire({
        title: 'Error!',
        text: 'Incorrect Password',
        type: 'error',
        timer: 2000,
      });
      return;
    }
    swal.fire({
      title: 'Success!',
      text: 'Information updated successfully',
      type: 'success',
      timer: 2000,
    });
  }

  async handlePasswords(): Promise<boolean> {
    const { password, newPassword, confirmPassword } = this.userData;
    if (!newPassword) {
      return true;
    }

    if (newPassword !== confirmPassword) {
      swal.fire({
        title: 'Passwords do not match',
        timer: 2000,
        showConfirmButton: false,
        type: 'warning',
      });
      return true;
    }

    if (!newPassword.match(/\d/)) {
      swal.fire({
        title: 'Password must include a number',
        timer: 2000,
        showConfirmButton: false,
        type: 'warning',
      });
      return true;
    }

    if (!newPassword.match(/[A-Z]/)) {
      swal.fire({
        title: 'Password must include a capital letter',
        timer: 2000,
        showConfirmButton: false,
        type: 'warning',
      });
      return true;
    }

    if (!newPassword.match(/[a-z]/)) {
      swal.fire({
        title: 'Password must include a lower case letter',
        timer: 2000,
        showConfirmButton: false,
        type: 'warning',
      });
      return true;
    }

    if (newPassword.length < 8) {
      swal.fire({
        title: 'Password must be at least 8 characters long',
        timer: 2000,
        showConfirmButton: false,
        type: 'warning',
      });
      return true;
    }

    if (!password) {
      swal.fire({
        title: 'You old password is required in order to set a new password',
        timer: 2000,
        showConfirmButton: false,
        type: 'warning',
      });
      return true;
    }

    const success = await this.authenticationService.setNewPassword(password, newPassword);
    if (!success) {
      swal.fire({
        title: 'Incorrect Password',
        timer: 2000,
        showConfirmButton: false,
        type: 'warning',
      });
    }
    return success;
  }

  async updateUser(): Promise<boolean> {
    this.loading = true;

    const update: Partial<UserModel> & HasId = { id: this.user.id };

    if (this.userData.email !== this.user.email) {
      update.email = this.userData.email;
    }

    if (this.userData.phoneNumber !== this.user.phoneNumber) {
      update.phoneNumber = this.userData.phoneNumber;
    }

    if (this.userData.firstName !== this.user.firstName) {
      update.firstName = this.userData.firstName;
    }

    if (this.userData.lastName !== this.user.lastName) {
      update.lastName = this.userData.lastName;
    }

    if (!Object.keys(update).length) {
      return true;
    }

    return this.ovAutoService
      .update({
        entity: UserModel,
        item: update,
      })
      .then(() => {
        return true;
      })
      .catch(() => {
        swal.fire({
          title: 'Something Went Wrong!',
          text: 'Please try again later',
          type: 'error',
          timer: 2000,
        });
        return false;
      })
      .finally(() => {
        this.userData.password = null;
        this.userData.newPassword = null;
        this.userData.confirmPassword = null;
      });
  }

  async onFileUpload(event: FileUpload): Promise<boolean> {
    if (event.target.files && event.target.files[0]) {
      const allowedTypes = ['image/png', 'image/jpeg'];

      const item = event.target.files[0];

      if (!allowedTypes.includes(item.type)) {
        swal.fire({
          title: 'Error!',
          text: 'Only Images are allowed ( JPG | PNG )',
          type: 'error',
          timer: 2000,
        });
        return false;
      }
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        const image = new Image();
        image.src = e.target.result as string;
        image.onload = () => {
          this.scaleImage(e.target.result as string, 320, 320).then(base64Image => {
            this.ovAutoService.apollo
              .mutate({
                mutation: gql(`
              mutation updateAvatarImage($id: Int!, $image: String!) {
                updateAvatarImage(id: $id, image: $image)
              }
            `),
                variables: {
                  id: this.user.id,
                  image: base64Image,
                },
                errorPolicy: 'ignore',
                fetchPolicy: 'no-cache',
              })
              .subscribe({
                next: () => {
                  window.location.reload();
                },
              });
          });

          return true;
        };
      };
      reader.readAsDataURL(event.target.files[0]);
    }
    return false;
  }

  detectChanges(event: CompiledFieldData) {
    clearTimeout(this.interval);
    this.interval = setTimeout(() => {
      this.detect.diff(this.form.data, event);
      const passwordField = this.detect.newChanges.find(field => field.propertyKey === 'password');
      if (passwordField) {
        this.detect.removeField(passwordField);
      }
    }, 400);
  }

  async scaleImage(base64: string, width: number, height: number): Promise<string> {
    return new Promise(resolve => {
      const image = new Image();
      image.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        canvas.width = width;
        canvas.height = height;

        // Fit
        // if (image.width === image.height) {
        //   ctx.drawImage(image, 0, 0, width, height);
        // } else if (image.width > image.height) {
        //   const imageHeight = (image.height / image.width) * height;
        //   ctx.drawImage(image, 0, 0, width, imageHeight);
        // } else {
        //   const imageWidth = (image.width / image.height) * width;
        //   ctx.drawImage(image, 0, 0, imageWidth, height);
        // }

        // Cover
        if (image.width === image.height) {
          ctx.drawImage(image, 0, 0, width, height);
        } else if (image.width > image.height) {
          const widthOverflow = ((image.width - image.height) / 2 / image.width) * width;
          ctx.drawImage(image, -widthOverflow, 0, width + widthOverflow * 2, height);
        } else {
          const heightOverflow = ((image.height - image.width) / 2 / image.height) * height;
          ctx.drawImage(image, 0, -heightOverflow, width, height + heightOverflow * 2);
        }

        // canvas.to
        resolve(canvas.toDataURL());
      };
      image.src = base64;
    });
  }
}
